import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { marketplaceAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Calendar, 
  Target, 
  DollarSign, 
  ArrowLeft,
  Briefcase,
  Clock,
  CheckCircle,
  RefreshCw,
  Package,
  ChevronDown,
  Upload,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

function MyBookings() {
  const navigate = useNavigate();
  const { user, isSeller } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(function() {
    if (isSeller) {
      fetchBookings();
    }
  }, [isSeller]);

  function fetchBookings(isRetry) {
    setLoading(true);
    if (!isRetry) {
      setError(null);
      setRetryCount(0);
    }
    
    marketplaceAPI.getMyBookings()
      .then(function(response) {
        if (response.data.success) {
          const bookingsData = response.data.data || [];
          setBookings(bookingsData);
          setError(null);
        } else {
          setError(response.data.error || 'Failed to fetch bookings');
        }
      })
      .catch(function(err) {
        console.error('Failed to fetch bookings:', err);
        // Auto-retry once on network error
        if (!isRetry && retryCount < 1) {
          setRetryCount(retryCount + 1);
          console.log('Retrying booking fetch...');
          setTimeout(function() {
            fetchBookings(true);
          }, 2000);
          return;
        }
        setError('Failed to connect to server. Please click Refresh to try again.');
      })
      .finally(function() {
        setLoading(false);
      });
  }

  function formatDate(dateStr) {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return dateStr;
    }
  }

  function formatCurrency(amount) {
    if (!amount && amount !== 0) return 'N/A';
    return '$' + parseFloat(amount).toLocaleString();
  }

  function formatBuyerId(buyerId, buyerName) {
    // Format: First initial of first word + First initial of last word + first 2 chars of ID + last 4 chars of ID
    // All in uppercase
    if (!buyerId) return 'N/A';
    
    let initials = '';
    if (buyerName) {
      const words = buyerName.trim().split(/\s+/);
      if (words.length >= 1) {
        initials += words[0].charAt(0).toUpperCase();
      }
      if (words.length >= 2) {
        initials += words[words.length - 1].charAt(0).toUpperCase();
      } else if (words.length === 1) {
        // If only one word, use first two characters
        initials += words[0].charAt(1) ? words[0].charAt(1).toUpperCase() : '';
      }
    }
    
    const first2 = buyerId.substring(0, 2).toUpperCase();
    const last4 = buyerId.slice(-4).toUpperCase();
    
    return initials + first2 + last4;
  }

  function calculateTotalCommit(booking) {
    if (!booking.booking || !Array.isArray(booking.booking)) return 0;
    let total = 0;
    for (let i = 0; i < booking.booking.length; i++) {
      total += booking.booking[i].leads_commit || 0;
    }
    return total;
  }

  function calculateTotalValue(booking) {
    if (!booking.booking || !Array.isArray(booking.booking)) return 0;
    let total = 0;
    for (let i = 0; i < booking.booking.length; i++) {
      const item = booking.booking[i];
      total += (item.leads_commit || 0) * (item.cpl || 0);
    }
    return total;
  }

  function formatCampaignId(id) {
    if (!id) return 'N/A';
    // Remove 'cmp_' prefix if present
    const cleanId = id.replace('cmp_', '');
    if (cleanId.length < 6) return cleanId;
    return cleanId.substring(0, 2) + 'xxx' + cleanId.slice(-4);
  }

  function renderBookingItems(items) {
    if (!items || !Array.isArray(items) || items.length === 0) {
      return <p className="text-slate-500 text-sm">No booking details available</p>;
    }

    const rows = [];
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      rows.push(
        <tr key={i} className="hover:bg-slate-50">
          <td className="px-4 py-3">
            <Badge className="bg-purple-100 text-purple-700 border-purple-200">
              {item.lead_type}
            </Badge>
          </td>
          <td className="px-4 py-3 text-right font-medium text-slate-900">
            {item.leads_commit}
          </td>
          <td className="px-4 py-3 text-right text-slate-600">
            {item.target}
          </td>
          <td className="px-4 py-3 text-right text-slate-600">
            {formatCurrency(item.cpl)}
          </td>
          <td className="px-4 py-3 text-right font-semibold text-emerald-600">
            {formatCurrency(item.leads_commit * item.cpl)}
          </td>
          <td className="px-4 py-3 text-right text-slate-600">
            {formatDate(item.end_date_commit)}
          </td>
        </tr>
      );
    }
    return rows;
  }

  function renderBookingCard(booking, index) {
    const totalCommit = calculateTotalCommit(booking);
    const totalValue = calculateTotalValue(booking);
    const campaignName = booking.campaign_name || '';
    const buyerName = booking.buyer_name || '';
    
    return (
      <AccordionItem key={index} value={'booking-' + index} className="border rounded-lg mb-4 overflow-hidden">
        <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-orange-50">
          <div className="flex items-center justify-between w-full pr-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-orange-600" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-slate-900">
                    {campaignName || buyerName || 'Campaign Booking'}
                  </span>
                  <Badge variant="outline" className="text-xs font-mono">
                    CID: {formatCampaignId(booking.cmp_id)}
                  </Badge>
                </div>
                <div className="flex items-center gap-4 text-sm text-slate-500 mt-1">
                  <span className="flex items-center gap-1">
                    <Target className="w-3 h-3" />
                    {totalCommit} leads
                  </span>
                  <span className="flex items-center gap-1">
                    <Package className="w-3 h-3" />
                    {booking.booking ? booking.booking.length : 0} types
                  </span>
                </div>
              </div>
            </div>
            <div className="text-right mr-4">
              <div className="text-xs text-slate-500">Total Value</div>
              <div className="text-lg font-bold text-emerald-600">{formatCurrency(totalValue)}</div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4 mb-4 mt-2">
            <div className="bg-orange-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <Target className="w-3 h-3 mr-1" />
                Total Committed
              </div>
              <div className="font-semibold text-slate-900">{totalCommit} leads</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <Package className="w-3 h-3 mr-1" />
                Lead Types
              </div>
              <div className="font-semibold text-slate-900">
                {booking.booking ? booking.booking.length : 0}
              </div>
            </div>
            <div className="bg-emerald-50 rounded-lg p-3">
              <div className="flex items-center text-xs text-slate-500 mb-1">
                <DollarSign className="w-3 h-3 mr-1" />
                Est. Revenue
              </div>
              <div className="font-semibold text-emerald-700">{formatCurrency(totalValue)}</div>
            </div>
          </div>

          {/* Booking Details Table */}
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-100">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-slate-700">Lead Type</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">You Commit</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Campaign Target</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">CPL</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Value</th>
                  <th className="px-4 py-2 text-right font-medium text-slate-700">Delivery Date</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {renderBookingItems(booking.booking)}
              </tbody>
            </table>
          </div>

          {/* Campaign End Date */}
          {booking.booking && booking.booking[0] && booking.booking[0].end_date && (
            <div className="mt-4 flex items-center justify-between text-sm">
              <div className="flex items-center text-slate-500">
                <Calendar className="w-4 h-4 mr-2" />
                Campaign End Date
              </div>
              <div className="font-medium text-slate-700">
                {formatDate(booking.booking[0].end_date)}
              </div>
            </div>
          )}

          {/* Upload Leads Button */}
          <div className="mt-5 pt-4 border-t border-slate-100 flex justify-end">
            <a 
              href="https://app.aalign.io/seller/leadupload" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button 
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-md hover:shadow-lg transition-all"
                data-testid={'upload-leads-btn-' + index}
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Leads
                <ExternalLink className="w-3 h-3 ml-2 opacity-70" />
              </Button>
            </a>
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  }

  function renderSkeletons() {
    const items = [];
    for (let i = 0; i < 3; i++) {
      items.push(
        <Card key={i}>
          <CardContent className="p-6 space-y-4">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      );
    }
    return items;
  }

  function renderBookings() {
    if (loading) {
      return renderSkeletons();
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">Failed to load bookings</h3>
          <p className="text-slate-500 mb-4">{error}</p>
          <Button variant="outline" onClick={fetchBookings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Retry
          </Button>
        </div>
      );
    }

    if (bookings.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-orange-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-700 mb-2">No bookings yet</h3>
          <p className="text-slate-500 mb-4">
            You haven't booked any campaigns yet. Browse buyer campaigns to get started.
          </p>
          <Button onClick={function() { navigate('/marketplace'); }} className="bg-orange-500 hover:bg-orange-600">
            Browse Campaigns
          </Button>
        </div>
      );
    }

    const items = [];
    for (let i = 0; i < bookings.length; i++) {
      items.push(renderBookingCard(bookings[i], i));
    }
    return (
      <Accordion type="single" collapsible className="w-full">
        {items}
      </Accordion>
    );
  }

  if (!isSeller) {
    return (
      <Layout>
        <div className="container mx-auto px-4 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold text-slate-700 mb-2">Access Denied</h2>
            <p className="text-slate-500">This page is only available for sellers.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 lg:px-8 py-8" data-testid="my-bookings-page">
        <Button variant="ghost" className="mb-6" onClick={function() { navigate('/marketplace'); }}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Marketplace
        </Button>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">My Bookings</h1>
            <p className="text-slate-600">
              View and manage your campaign commitments
            </p>
          </div>
          <Button variant="outline" onClick={fetchBookings} disabled={loading}>
            <RefreshCw className={'w-4 h-4 mr-2 ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </Button>
        </div>

        {/* Stats Summary */}
        {!loading && !error && bookings.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Briefcase className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Active Bookings</div>
                  <div className="text-2xl font-bold text-slate-900">{bookings.length}</div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Total Leads Committed</div>
                  <div className="text-2xl font-bold text-slate-900">
                    {bookings.reduce(function(sum, b) { return sum + calculateTotalCommit(b); }, 0)}
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <div className="text-sm text-slate-500">Total Expected Revenue</div>
                  <div className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(bookings.reduce(function(sum, b) { return sum + calculateTotalValue(b); }, 0))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Bookings List */}
        <div className="space-y-6">
          {renderBookings()}
        </div>
      </div>
    </Layout>
  );
}

export default MyBookings;
